/**
 * Fetch compare-screen catalog from Supabase (products + specs + comparison profiles).
 * Uses REACT_APP_SUPABASE_URL + REACT_APP_SUPABASE_ANON_KEY via supabaseClient.
 */
import { supabase, isSupabaseConfigured } from './supabaseClient';

const COMPARISON_GROUP = 'screens';

const SPEC_KEYS_FOR_COMPARE = [
  'display_diagonal_inches_exact',
  'resolution_width_px',
  'resolution_height_px',
  'touch_supported',
  'keypad_supported',
  'hdmi_out_supported',
  'hdmi_in_supported',
  'bnc_composite_in_supported',
  'garmin_marine_network_port_count',
  'sonar_chirp_supported',
  'sonar_clearvu_supported',
  'sonar_sidevu_supported',
  'typical_power_draw_12v_a',
];

function pickSpecValue(row) {
  if (row.spec_value_numeric !== null && row.spec_value_numeric !== undefined) {
    return row.spec_value_numeric;
  }
  if (row.spec_value_boolean !== null && row.spec_value_boolean !== undefined) {
    return row.spec_value_boolean;
  }
  if (row.spec_value_text !== null && row.spec_value_text !== undefined) {
    return row.spec_value_text;
  }
  return null;
}

function buildSpecsMap(rowsForProduct) {
  const specs = Object.fromEntries(SPEC_KEYS_FOR_COMPARE.map((k) => [k, null]));
  const wanted = new Set(SPEC_KEYS_FOR_COMPARE);
  for (const row of rowsForProduct) {
    if (!wanted.has(row.spec_key)) continue;
    specs[row.spec_key] = pickSpecValue(row);
  }
  return specs;
}

function emptyProfileShape() {
  return {
    badge_label: null,
    who_its_for: null,
    family_guidance_note: null,
    boat_use_summary: null,
    upgrade_path_note: null,
    live_sonar_summary: null,
  };
}

/**
 * Loads products that have compare_id set, with specs and comparison profile rows.
 * @returns {Promise<Array<{
 *   id: string,
 *   product_id: string,
 *   title: string | null,
 *   slug: string | null,
 *   image_url: string | null,
 *   msrp: number | null,
 *   specs: Record<string, number | boolean | string | null>,
 *   profile: {
 *     badge_label, who_its_for, family_guidance_note, boat_use_summary, upgrade_path_note, live_sonar_summary
 *   }
 * }>>}
 */
export async function fetchCompareScreensProducts() {
  if (!isSupabaseConfigured() || !supabase) {
    return [];
  }

  const { data: products, error: productsError } = await supabase
    .from('products')
    .select('id, compare_id, name, slug, image_url, msrp')
    .not('compare_id', 'is', null);

  if (productsError) {
    throw new Error(productsError.message || 'Failed to fetch products for compare screens');
  }

  const list = Array.isArray(products) ? products : [];
  if (list.length === 0) {
    return [];
  }

  const productIds = list.map((p) => p.id);

  const { data: specRows, error: specsError } = await supabase
    .from('product_specs')
    .select('product_id, spec_key, spec_value_numeric, spec_value_boolean, spec_value_text')
    .in('product_id', productIds);

  if (specsError) {
    throw new Error(specsError.message || 'Failed to fetch product_specs for compare screens');
  }

  const { data: profileRows, error: profilesError } = await supabase
    .from('product_comparison_profiles')
    .select(
      'product_id, badge_label, who_its_for, family_guidance_note, boat_use_summary, upgrade_path_note, live_sonar_summary, comparison_group',
    )
    .in('product_id', productIds)
    .eq('comparison_group', COMPARISON_GROUP);

  if (profilesError) {
    throw new Error(profilesError.message || 'Failed to fetch product_comparison_profiles for compare screens');
  }

  const specsByProductId = new Map();
  for (const row of specRows || []) {
    const pid = row.product_id;
    if (!specsByProductId.has(pid)) specsByProductId.set(pid, []);
    specsByProductId.get(pid).push(row);
  }

  const profileByProductId = new Map();
  for (const row of profileRows || []) {
    profileByProductId.set(row.product_id, row);
  }

  return list.map((p) => {
    const prof = profileByProductId.get(p.id);
    const base = emptyProfileShape();
    if (prof) {
      base.badge_label = prof.badge_label ?? null;
      base.who_its_for = prof.who_its_for ?? null;
      base.family_guidance_note = prof.family_guidance_note ?? null;
      base.boat_use_summary = prof.boat_use_summary ?? null;
      base.upgrade_path_note = prof.upgrade_path_note ?? null;
      base.live_sonar_summary = prof.live_sonar_summary ?? null;
    }

    const msrp =
      p.msrp !== null && p.msrp !== undefined && p.msrp !== ''
        ? Number(p.msrp)
        : null;

    return {
      id: p.compare_id,
      product_id: p.id,
      title: p.name ?? null,
      slug: p.slug ?? null,
      image_url: p.image_url ?? null,
      msrp: msrp !== null && !Number.isNaN(msrp) ? msrp : null,
      specs: buildSpecsMap(specsByProductId.get(p.id) || []),
      profile: base,
    };
  });
}
